import { api } from "./client";

/**
 * What the signed-in user must do before using the app — mirrors the server's
 * OnboardingState (services/onboardingService.ts).
 */
export type OnboardingState =
  | {
      step: "ready";
      organizationName: string | null;
      organizationRole: "admin" | "member" | null;
      needsTeamAccess: boolean;
      /** The organization's admins, so a member knows whom to ask. */
      admins: { name: string; email: string }[];
    }
  | { step: "name_organization"; suggestedName: string }
  | {
      step: "choose_plan";
      organizationName: string;
      people: number;
      pricing: { freePeopleLimit: number; teamPricePerSeatUsd: number; trialDays: number };
    }
  | {
      step: "join_or_create";
      organization: { name: string };
      joinMode: "auto" | "request";
      request: { status: "pending" | "declined"; createdAt: string } | null;
      suggestedName: string;
    }
  | { step: "create_organization"; suggestedName: string }
  | {
      /** How will AI run this person's work? Skippable; asked once. */
      step: "choose_ai";
      organizationName: string;
      organizationRole: "admin" | "member";
    }
  | {
      /** An org admin connects the company's GitHub (the CompBox app). Skippable; asked once. */
      step: "connect_github";
      organizationName: string;
      installUrl: string;
    };

export const onboardingApi = {
  /** GET /api/v1/onboarding */
  state: () => api.get<{ data: OnboardingState }>("/onboarding"),
  /** POST /api/v1/onboarding/join-request — `joined` is true for an auto-join domain. */
  requestToJoin: () => api.post<{ data: { joined: boolean } }>("/onboarding/join-request", {}),
  /** DELETE /api/v1/onboarding/join-request */
  cancelRequest: () => api.delete("/onboarding/join-request"),
  /** POST /api/v1/onboarding/team-access-request — email the admins asking for a team. */
  requestTeamAccess: () =>
    api.post<{ data: { emailed: number } }>("/onboarding/team-access-request", {}),
  /** POST /api/v1/onboarding/plan — an org admin picks Free or Team. */
  selectPlan: (plan: "free" | "team") =>
    api.post<{ data: OnboardingState }>("/onboarding/plan", { plan }),
  /** POST /api/v1/onboarding/ai-setup — finish or skip the AI setup step. */
  completeAiSetup: () => api.post<{ data: OnboardingState }>("/onboarding/ai-setup", {}),
  /** POST /api/v1/onboarding/github-setup — finish or skip "Connect GitHub". */
  completeGitHubSetup: () => api.post<{ data: OnboardingState }>("/onboarding/github-setup", {}),
  /** POST /api/v1/onboarding/managed-ai-waitlist */
  joinManagedAiWaitlist: () => api.post("/onboarding/managed-ai-waitlist", {}),
  /** POST /api/v1/onboarding/organization */
  createOrganization: (name: string) =>
    api.post<{ data: { organizationId: string } }>("/onboarding/organization", { name }),
};
